import React, { useState } from 'react';
import { ShieldCheck, Smartphone, Zap, CalendarCheck, MapPin } from 'lucide-react';
import { colorHex, photoUrl, creditPlan, fmtPrice } from './utils';

export const Hero = ({ mall, branch, featured, currency, rate, onBuyCredit, onReserve }) => {
  const colors = featured?.specs?.colors?.length ? featured.specs.colors : ['Negro', 'Azul', 'Verde', 'Lila'];
  const [sel, setSel] = useState(0);
  const allowCredit = mall?.vertical_flags?.enable_credit_plans !== false;
  const plan = featured ? creditPlan(featured.price, mall, 0, featured.credit_plan) : null;
  const img = photoUrl(featured);
  return (
    <section id="top" className="relative overflow-hidden cb-hero" data-testid="mall-hero">
      <div className="max-w-7xl mx-auto px-4 pt-10 pb-16 lg:pt-16 grid lg:grid-cols-2 gap-10 items-center">
        <div className="cb-rise">
          <span className="inline-block text-xs font-black uppercase tracking-widest bg-[#FFC857] text-[#0A192F] px-3 py-1 rounded-full">{branch ? branch.name : mall.tagline}</span>
          <h1 className="cb-display text-5xl sm:text-6xl lg:text-7xl text-white mt-4 leading-[0.9]">{branch?.hero_title || mall.hero_title}</h1>
          <p className="text-white/75 mt-4 max-w-lg text-base">{branch?.hero_subtitle || mall.hero_subtitle}</p>
          {featured && (
            <div className="mt-6 inline-flex items-center gap-3 rounded-2xl border-2 border-[#FFC857] px-4 py-2 bg-[#0A192F]/60">
              <span className="cb-display text-3xl text-white">{featured.name}</span>
              <span className="cb-display text-2xl text-[#FFC857]" data-testid="hero-weekly-price">{allowCredit ? `${fmtPrice(plan.default.amount, currency, rate)} c/u` : fmtPrice(featured.price, currency, rate)}</span>
            </div>
          )}
          <div className="mt-6 flex flex-wrap gap-3">
            {allowCredit && <button onClick={() => featured && onBuyCredit(featured)} data-testid="hero-buy-credit-btn" className="cb-btn-primary">Comprar a Crédito</button>}
            <button onClick={() => featured && onReserve(featured)} data-testid="hero-reserve-btn" className={allowCredit ? 'cb-btn-ghost' : 'cb-btn-primary'}><MapPin size={16} /> {allowCredit ? 'Reservar y Retirar' : 'Reservar / Comprar'}</button>
          </div>
          <div className="mt-8 flex flex-wrap gap-4 text-white/80 text-sm">
            <span className="flex items-center gap-2"><ShieldCheck className="text-[#FFC857]" size={18} /> Garantía y respaldo</span>
            <span className="flex items-center gap-2"><Smartphone className="text-[#FFC857]" size={18} /> Productos originales</span>
          </div>
        </div>
        <div className="relative flex flex-col items-center cb-rise" style={{ animationDelay: '120ms' }}>
          <div className="cb-orb" />
          <div className="relative w-64 sm:w-80 aspect-[3/4] rounded-[2.5rem] border-[6px] border-[#FFC857] bg-gradient-to-b from-[#1C2541] to-[#0A192F] shadow-[0_30px_80px_rgba(0,242,254,0.25)] overflow-hidden grid place-items-center transition-colors duration-500"
            style={{ boxShadow: `0 30px 90px ${colorHex(colors[sel])}66` }} data-testid="hero-device-preview">
            {img ? <img src={img} alt={featured?.name} className="w-full h-full object-contain p-4" /> : (mall?.logo_url ? <img src={mall.logo_url} alt="" className="w-1/2 opacity-60 object-contain" /> : <div className="w-3/4 h-32 grid place-items-center text-white/30 text-sm">Sin imagen</div>)}
            <div className="absolute inset-x-0 bottom-0 h-1/3" style={{ background: `linear-gradient(to top, ${colorHex(colors[sel])}aa, transparent)` }} />
          </div>
          <div className="mt-5 flex gap-3" data-testid="hero-swatches">
            {colors.map((c, i) => (
              <button key={c} title={c} onClick={() => setSel(i)} data-testid={`hero-color-swatch-${String(c).toLowerCase()}`}
                className={`w-9 h-9 rounded-full border-2 transition-transform ${sel === i ? 'border-[#FFC857] scale-110' : 'border-white/30'}`} style={{ background: colorHex(c) }} />
            ))}
          </div>
          <p className="text-white/60 text-xs mt-2">{colors[sel]}</p>
        </div>
      </div>
    </section>
  );
};

export const Bento = ({ onPrequal, onReserve, onPortal, mall }) => {
  const allowCredit = mall?.vertical_flags?.enable_credit_plans !== false;
  return (
  <section className="max-w-7xl mx-auto px-4 -mt-6 relative z-10 grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="mall-bento">
    {allowCredit && (
    <button onClick={onPrequal} className="md:col-span-5 text-left rounded-3xl p-6 bg-[#FFC857] text-[#0A192F] cb-card-hover" data-testid="bento-prequal">
      <Zap size={28} /><p className="cb-display text-3xl mt-3">¿Calificas? Descúbrelo en 1 minuto</p>
      <p className="text-sm mt-2 opacity-80">Cédula, situación laboral e ingresos. Respuesta inmediata.</p>
    </button>)}
    <button onClick={onReserve} className={`${allowCredit ? 'md:col-span-4' : 'md:col-span-8'} text-left rounded-3xl p-6 bg-[#0F52BA] text-white cb-card-hover`} data-testid="bento-reserve">
      <MapPin size={28} className="text-[#FFC857]" /><p className="cb-display text-3xl mt-3">Reserva y retira</p>
      <p className="text-sm mt-2 text-white/80">Aparta tu producto y retíralo en tienda o recíbelo por delivery.</p>
    </button>
    <button onClick={onPortal} className="md:col-span-3 text-left rounded-3xl p-6 bg-[#1C2541] text-white border border-white/10 cb-card-hover" data-testid="bento-portal">
      <CalendarCheck size={28} className="text-[#00F2FE]" /><p className="cb-display text-3xl mt-3">{allowCredit ? 'Mis cuotas' : 'Mi cuenta'}</p>
      <p className="text-sm mt-2 text-white/70">{allowCredit ? 'Consulta tus pagos y reporta tu Pago Móvil.' : 'Consulta tus reservas y pedidos.'}</p>
    </button>
  </section>
  );
};

export default Hero;
